(() => {
  if (window.PPMediaLoader) return;

  const selector = 'video[data-pp-lazy-video]';
  const tracked = new Set();
  const visible = new Map();
  const selected = new WeakMap();
  const manual = new WeakSet();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;

  function load(video) {
    if (!video || video.dataset.ppLoaded === 'true') return;
    video.dataset.ppLoaded = 'true';
    if (video.dataset.ppPoster && !video.poster) {
      video.poster = video.parentElement?.querySelector('img[data-thumbnail-index]')?.currentSrc || video.dataset.ppPoster;
    }
    if (video.dataset.ppSrc) video.src = video.dataset.ppSrc;
    video.querySelectorAll('source[data-pp-src]').forEach(source => {
      source.src = source.dataset.ppSrc;
    });
    video.load();
  }

  function play(video) {
    load(video);
    const result = video.play();
    if (result) result.catch(error => {
      if (error.name !== 'AbortError') video.controls = true;
    });
  }

  function group(video) {
    return video.closest('[data-pp-video-group], .carousel-wrapper') || video;
  }

  function canAutoplay() {
    return !document.hidden && !reducedMotion.matches && !navigator.connection?.saveData;
  }

  function updatePlayback() {
    frame = 0;
    if (!observer) {
      if (document.hidden) tracked.forEach(video => video.pause());
      return;
    }
    const winners = new Map();
    if (canAutoplay()) {
      visible.forEach((entry, video) => {
        if (!video.isConnected || video.dataset.ppAutoplay !== 'true' || entry.intersectionRatio < 0.25) return;
        const key = group(video);
        const previous = winners.get(key);
        if (!previous || entry.intersectionRatio > visible.get(previous).intersectionRatio) winners.set(key, video);
      });
      winners.forEach((video, key) => {
        const requested = selected.get(key);
        if (visible.get(requested)?.intersectionRatio >= 0.25) winners.set(key, requested);
      });
    }
    tracked.forEach(video => {
      if (!video.isConnected) {
        observer?.unobserve(video);
        tracked.delete(video);
        visible.delete(video);
        return;
      }
      const entry = visible.get(video);
      const inView = entry && entry.isIntersecting && entry.intersectionRatio >= 0.25;
      const manualPlayback = !canAutoplay() && inView && !document.hidden && manual.has(video) && selected.get(group(video)) === video;
      if (video.dataset.ppAutoplay === 'true' && !canAutoplay()) video.controls = true;
      if (video.dataset.ppAutoplay === 'true' && winners.get(group(video)) === video) {
        if (video.paused) play(video);
      } else if (!inView || document.hidden || (video.dataset.ppAutoplay === 'true' && !manualPlayback)) {
        if (!video.paused) video.pause();
      }
    });
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(updatePlayback);
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      visible.set(entry.target, entry);
      // Prime only the visible poster, not the entire offscreen carousel.
      if (entry.isIntersecting && entry.intersectionRatio >= 0.25 && !entry.target.poster && !entry.target.hasAttribute('data-pp-tap-only')) {
        load(entry.target);
        entry.target.preload = 'metadata';
      }
    });
    schedule();
  }, { threshold: [0, 0.25, 0.5, 0.75, 1] }) : null;

  function scan(root = document) {
    const videos = root.matches?.(selector) ? [root] : root.querySelectorAll(selector);
    videos.forEach(video => {
      if (tracked.has(video)) return;
      tracked.add(video);
      video.addEventListener('pointerdown', () => load(video));
      video.addEventListener('keydown', () => load(video));
      if (video.dataset.ppAutoplay === 'true' && !canAutoplay()) video.controls = true;
      if (observer) observer.observe(video);
      else { if (!video.hasAttribute('data-pp-tap-only')) load(video); video.controls = true; }
    });
  }

  window.PPMediaLoader = { load, play, scan };
  document.addEventListener('pointerdown', event => {
    const button = event.target.closest?.('[data-sound-toggle]');
    const video = button ? button.closest('article')?.querySelector(selector) : event.target.closest?.(selector);
    if (video) {
      selected.set(group(video), video);
      manual.add(video);
      load(video);
      schedule();
    }
  }, true);
  document.addEventListener('visibilitychange', schedule);
  reducedMotion.addEventListener?.('change', schedule);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
})();
